import { ref, computed } from 'vue';
import * as Sentry from '@sentry/vue';
import { useUserStore } from '@/stores/user';
import { getAttributionHeaders } from '@/util/param-persistence';

export function useStripeCheckout() {
  const stripePaymentCompleted = ref(false);
  const aetherPromoCode = ref('');
  const userStore = useUserStore();

  const isStripePaymentEnabled = computed(() => userStore.apis?.learnerData?.useAetherFlow || false);

  function handleStripePaymentAuthorized() {
    // Stripe payment form is ready
  }

  function handleStripePaymentCompleted(
    event,
    { checkoutSelectedCourseRef, invoiceNumber, amount, captureEcomDataLayer }
  ) {
    stripePaymentCompleted.value = true;

    // Build a Stripe response object compatible with transactionEnrollment
    // Note: do NOT include paymentMethod:'stripe' here — that key is only used
    // to trigger the initial Stripe confirm in next(). Including it would cause
    // next() to re-trigger handlePayment() after enrollment completes.
    const stripeResponse = {
      id: event.sessionId || event.id,
      payer: { payment_method: 'stripe' },
      transactions: [
        {
          invoice_number: event.metadata?.invoiceNumber || invoiceNumber,
          amount: { total: String(event.amount || amount) }
        }
      ]
    };

    // Trigger the enrollment/invoicing/transaction flow on CheckoutSelectedCourse
    if (checkoutSelectedCourseRef) {
      checkoutSelectedCourseRef.transactionEnrollment(stripeResponse);
    }

    captureEcomDataLayer('purchase');
  }

  function handleStripePaymentCancelled() {
    // Payment was cancelled or failed - user stays on page
  }

  function handlePromoValidated(promoData) {
    aetherPromoCode.value = promoData.code;
  }

  function handlePromoRemoved() {
    aetherPromoCode.value = '';
  }

  // Clears the learner's Aether cart. The ulc-api-hub `/api/stripe/cart/remove-item`
  // handler iterates over all current cart items, so the call doesn't need an
  // offering id — pass one only when the intent is "remove this specific item"
  // (it's still accepted by the body but the backend ignores it for the loop).
  async function clearStripeCart(courseOfferingId) {
    if (!isStripePaymentEnabled.value) return;

    const { default: customOAuthService } = await import('../../services/custom-oauth.service');
    const oauthToken = customOAuthService.getAccessToken();
    if (!oauthToken) return;

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const xApiKey = import.meta.env.VITE_X_API_KEY;

    // Include the learner's ULC contact PK so any backend Sentry capture for
    // this request can attribute the error to the user. Matches the value
    // /api/stripe/checkout already sends.
    const learnerData = userStore?.apis?.learnerData;
    const contactId = learnerData?._contactId || learnerData?.contactId || '';
    const body = {
      ...(courseOfferingId && { offeringId: courseOfferingId }),
      ...(contactId && { contactId })
    };

    try {
      await fetch(`${apiBaseUrl}/api/stripe/cart/remove-item`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${oauthToken}`,
          'Content-Type': 'application/json',
          'x-api-key': xApiKey,
          ...getAttributionHeaders()
        },
        body: JSON.stringify(body)
      });
    } catch (err) {
      console.error('[Stripe] Failed to clear cart:', err);
      Sentry.captureException(err, {
        tags: { source: 'stripe_checkout', phase: 'clearCart' },
        extra: { offeringId: courseOfferingId }
      });
    }
  }

  function generateInvoiceNumber() {
    let today = new Date().toISOString().slice(0, 10).replace(/-/g, '').slice(2);
    let prefix = import.meta.env.VITE_ENV_HOOK === 'prod' ? 'PRODOSPP' : 'OSPP';
    let random2Char = '';
    let random3Char = '';
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let charactersLength = characters.length;

    for (let i = 0; i < 2; i++) {
      random2Char += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    for (let i = 0; i < 3; i++) {
      random3Char += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return `${prefix}-${today}${Math.floor(Math.random() * 10)}${random2Char}${Math.floor(Math.random() * 100)}${random3Char}`;
  }

  function triggerStripePayment(stripeCheckoutRef) {
    if (stripeCheckoutRef) {
      stripeCheckoutRef.handlePayment();
    }
  }

  return {
    stripePaymentCompleted,
    aetherPromoCode,
    isStripePaymentEnabled,
    handleStripePaymentAuthorized,
    handleStripePaymentCompleted,
    handleStripePaymentCancelled,
    handlePromoValidated,
    handlePromoRemoved,
    clearStripeCart,
    generateInvoiceNumber,
    triggerStripePayment
  };
}
